/* eslint-disable complexity */
import { DistributorInfoDrawer } from '@/components/blocks/DistributorInfoDrawer';
import {
	distributorNavbarArrowsSX,
	distributorNavbarAvatarFrameSX,
	distributorNavbarAvatarSX,
	distributorNavbarButtonSX,
	distributorNavbarContentSX,
	distributorNavbarFrameSX,
	distributorNavbarHelpIconSX,
	distributorNavbarNameSX,
	distributorNavbarTutorialButtonSX,
	distributorNavbarWarningIconSX,
} from '@/components/blocks/DistributorNavBar/styles/main';
import { useMobileHeaderQuery } from '@/components/content/Header';
import { useDpCatalogoDistributor } from '@/data/Content/DpCatalogo';
import { useOnboardingTour } from '@/data/Content/OnboardingTour';
import { usePageLoaded } from '@/data/Content/PageLoaded';
import { useProductDetailsTour } from '@/data/Content/ProductDetailsTour';
import { usePageContext } from '@/data/context/page';
import BookIcon from '@mui/icons-material/Book';
import HelpIcon from '@mui/icons-material/Help';
import WarningIcon from '@mui/icons-material/Warning';
import { Avatar, Box, ButtonBase, Stack, Typography } from '@mui/material';
import { Fragment, useEffect, useRef, useState } from 'react';

const homePathnames = [
	'/es-ES/',
	'/',
	'/es-ES/dpcatalog-home',
	'/dpcatalog-home',
	'/es-ES/home-es',
	'/home-es',
];

const useIsHomePage = () => {
	const [isHomePage, setIsHomePage] = useState(false);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const path = window.location.pathname;
			setIsHomePage(homePathnames.includes(path));
		}
	}, []);

	return isHomePage;
};

const useIsProductDetails = () => {
	const { rawData } = usePageContext();
	return rawData?.tokenName === 'ProductToken';
};

export const DistributorNavBar = () => {
	const [openInfoDrawer, setOpenInfoDrawer] = useState(false);
	const hasStartedTour = useRef(false);
	const { data, isLoading: isLoadingDistributor } = useDpCatalogoDistributor();
	const isMobile = useMobileHeaderQuery();
	const pageLoaded = usePageLoaded();
	const { onAutoStartOnboardingTour, onStartOnboardingTour } = useOnboardingTour();
	const { onStartProductDetailsTour, onAutoStartProductDetailsTour } = useProductDetailsTour();
	const isHomePage = useIsHomePage();
	const isProductDetails = useIsProductDetails();

	useEffect(() => {
		if (!data || !pageLoaded || hasStartedTour.current || (!isHomePage && !isProductDetails)) {
			return;
		}

		const timer = setTimeout(() => {
			isHomePage && onAutoStartOnboardingTour();
			isProductDetails && onAutoStartProductDetailsTour();
			hasStartedTour.current = true;
		}, 1500);

		return () => clearTimeout(timer);
	}, [
		data,
		pageLoaded,
		hasStartedTour,
		isHomePage,
		onAutoStartOnboardingTour,
		isProductDetails,
		onAutoStartProductDetailsTour,
	]);

	const handleOpenInfoDrawer = () => {
		setOpenInfoDrawer(true);
	};

	const handleCloseInfoDrawer = () => {
		setOpenInfoDrawer(false);
	};

	const handleOpenTutorial = () => {
		if (isHomePage) {
			onStartOnboardingTour();
		} else if (isProductDetails) {
			onStartProductDetailsTour();
		}
	};

	if (isLoadingDistributor || !data) {
		return null;
	}

	const distributorName = `${data.name || ''} ${data.lastName || ''}`.trim();

	const showTutorialButton = isHomePage || isProductDetails;

	return (
		<Fragment>
			<Box sx={distributorNavbarFrameSX}>
				<Box
					id="distributor-navbar-frame"
					sx={{
						display: 'flex',
						flex: 1,
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Stack sx={distributorNavbarContentSX}>
						{distributorName ? (
							<Fragment>
								<Stack sx={distributorNavbarAvatarFrameSX}>
									<Avatar sx={distributorNavbarAvatarSX} />
								</Stack>
								<Typography sx={distributorNavbarNameSX}>
									{`Distribuidor: ${distributorName}`}
								</Typography>
							</Fragment>
						) : (
							<Fragment>
								<Stack sx={distributorNavbarAvatarFrameSX}>
									<WarningIcon sx={distributorNavbarWarningIconSX} />
								</Stack>
								<Typography sx={distributorNavbarNameSX}>
									{'Contacta a tu distribuidor más cercano'}
								</Typography>
							</Fragment>
						)}
					</Stack>
					<Stack
						sx={
							isMobile
								? distributorNavbarButtonSX
								: { ...distributorNavbarButtonSX, paddingRight: '20px' }
						}
					>
						{showTutorialButton ? (
							<ButtonBase sx={distributorNavbarTutorialButtonSX} onClick={handleOpenTutorial}>
								<BookIcon sx={distributorNavbarHelpIconSX} />
								{'Tutorial'}
							</ButtonBase>
						) : null}
						<ButtonBase onClick={handleOpenInfoDrawer}>
							<HelpIcon sx={distributorNavbarHelpIconSX} />
							{'Ayuda'}
						</ButtonBase>
					</Stack>
				</Box>
				<Box sx={distributorNavbarArrowsSX} />
			</Box>
			<DistributorInfoDrawer
				open={openInfoDrawer}
				distributorName={distributorName}
				onClose={handleCloseInfoDrawer}
			/>
		</Fragment>
	);
};
